import React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../DesignSystem/colors';
import { Breakpoints, shimmerCSS } from '../../DesignSystem/design-system';
const Container = styled.div `
  box-sizing: border-box;
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  border-radius: ${(props) => props.borderRadius || '0'};
  height: ${(props) => props.height || 'auto'};
  width: ${(props) => props.width || 'auto'};
  text-align: ${(props) => props.textAlign || 'left'};
  ${(props) => props.borderBottom && `border-bottom: ${props.borderBottom};`}
  ${(props) => props.borderTop && `border-top: ${props.borderTop};`}
  ${(props) => props.position && `position: ${props.position};`}
  ${(props) => props.border && `border: ${props.border};`}
  ${(props) => props.onClick && `cursor: pointer;`}
  ${(props) => props.cursor && `cursor: ${props.cursor};`}
  ${(props) => props.minHeight && `min-height: ${props.minHeight};`}
  ${(props) => props.maxHeight && `max-height: ${props.maxHeight};`}
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth};`}
  ${(props) => props.opacity && `opacity: ${props.opacity};`}
  ${(props) => props.zIndex &&
    `
    z-index: ${props.zIndex};
    position: inherit;
  `}

  ${(props) => props.isLoading &&
    css `
      border: none;
      background-color: ${colors.background.backgroundPrimary};
      * {
        visibility: hidden;
      }
      border-radius: 4px;
      ${shimmerCSS}
    `}

  @media (max-width: ${Breakpoints.Mobile}) {
    ${(props) => props.mobileBorderRadius && `border-radius: ${props.mobileBorderRadius};`}
    ${(props) => props.mobileWidth && `width: ${props.mobileWidth};`}
    ${(props) => props.mobileBorder && `border: ${props.mobileBorder};`}
  }
`;
/**
 * A container component for holding child components.
 *
 * @param {React.ReactNode} props.children - The child components that ImpContainer will wrap around.
 * @param {string} [props.backgroundColor='transparent'] - The background color of the container.
 * @param {string} [props.borderRadius='0'] - The border radius of the container.
 * @param {string} [props.textAlign='left'] - The text alignment within the container.
 * @param {string} [props.height='auto'] - The height of the container.
 * @param {string} [props.width='auto'] - The width of the container.
 * @param {string} [props.dataTestId='container'] - The data-testid of the container.
 * @param {string} [props.cursor='auto'] - The cursor style of the container.
 * @param {string} props.borderBottom - The style of the bottom border of the container.
 * @param {string} props.border - The style of the border of the container.
 * @param {string} props.position - The position of the container.
 * @param {tabIndex} props.tabIndex - The tabIndex of the container.
 * @param {React.RefObject<HTMLDivElement>} props.containerRef - The ref of the container.
 * @param {() => void} props.onClick - The onClick function of the container.
 * @param {object} props.style - Style overrides
 *
 * @returns The ImpContainer component.
 */
export default function ImpContainer({ dataTestId = 'container', onClick = undefined, containerRef, tabIndex, ...args }) {
    let calculatedTabIndex = tabIndex;
    if (typeof onClick === 'function' && tabIndex === undefined) {
        calculatedTabIndex = 0;
    }
    const handleClick = (event) => {
        if (typeof onClick === 'function') {
            onClick(event);
        }
    };
    return (React.createElement(Container, { "data-testid": dataTestId, ref: containerRef, onClick: onClick ? handleClick : undefined, tabIndex: calculatedTabIndex, onKeyDown: (e) => {
            if (e.key === 'Enter' && typeof onClick === 'function') {
                const syntheticEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });
                handleClick(syntheticEvent);
            }
        }, ...args }, args.children));
}
//# sourceMappingURL=ImpContainer.js.map